import { AbstractPlainTextItem } from "./AbstractPlainTextItem";
import { TextItemOption } from "./TextItemOption";
import { IOptionSelectable } from "./IOptionSelectable";
import { IEmptyValidatable } from "./IEmptyValidatable";
import { INameGroupEmptyValidatable } from "./INameGroupEmptyValidatable";
import { IPasteRequestable } from "./IPasteRequestable";
import { RelationalActionParameter } from "./RelationalActionParameter";
import { IPasteApplicable } from "./IPasteApplicable";
import { IFontSizeAutoAdjustable } from "./IFontSizeAutoAdjustable";
import { AutoNotePageKinds } from "../AutoNotePageKinds";
import { IExternalScriptAssociatable } from "./IExternalScriptAssociatable";
import { IRelatedExternalScriptExecutable } from "./IRelatedExternalScriptExecutable";
import { IPatientProfileInformationApplicable } from "./IPatientProfileInformationApplicable";
import { PatientProfileInformation } from "../../patient/PatientProfileInformation";
import { PatientProfileItem } from "../../patient/PatientProfileItem";
import { TextureKinds } from "../../drawing";
import { IValueForCalculateExtractable } from "./IValueForCalculateExtractable";
import { IJoinTextAssosiatable } from "./IJoinTextAssosiatable";

export abstract class AbstractOptionSelectableTextItem extends AbstractPlainTextItem implements IOptionSelectable, IEmptyValidatable, INameGroupEmptyValidatable, IPasteRequestable, IFontSizeAutoAdjustable, IExternalScriptAssociatable, IRelatedExternalScriptExecutable, IPatientProfileInformationApplicable, IValueForCalculateExtractable, IJoinTextAssosiatable {
  constructor(typeKey: string, value: any, valueSource: string | number) {
    // 本来であればvalueの更新実装と合わせたいが、派生クラスの初期化時に指定される初期値と違うので、派生クラスで指定させる事とする。
    super(typeKey, value);
    this._originalValueSource = valueSource;
  }

  protected readonly _originalValueSource: string | number; // string型はフリー入力を、number型はoptionsの選択インデックスを示す

  get originalValueSource(): string | number {
    return this._originalValueSource;
  }

  public label: string = "";
  public code: string = null;
  public score: number = null;
  public options: TextItemOption[] = [];
  public enableSingleEmptyCheck: boolean = false;
  public enableEmptyCheckConfirmation: boolean = false;
  public alertGroupKey: string = null;
  public pasteDestinations: RelationalActionParameter[] = [];
  public parentAlertInformations: RelationalActionParameter[] = [];
  /** フォントサイズ自動調整機能 */
  public enabledAutoAdjustFontSize: boolean = false;
  public autoNotePageSize: number = null;
  public autoNotePageKind: AutoNotePageKinds = null;
  public enableNameGroupEmptyCheck: boolean = false;

  protected applyMembers(clone: AbstractOptionSelectableTextItem): void {
    super.applyMembers(clone);
    clone.label = this.cloneValue(this.label);
    clone.code = this.cloneValue(this.code);
    clone.score = this.cloneValue(this.score);
    clone.options = this.cloneValue(this.options);
    clone.enableSingleEmptyCheck = this.cloneValue(this.enableSingleEmptyCheck);
    clone.enableEmptyCheckConfirmation = this.cloneValue(this.enableEmptyCheckConfirmation);
    clone.alertGroupKey = this.cloneValue(this.alertGroupKey);
    clone.pasteDestinations = this.cloneValue(this.pasteDestinations);
    clone.parentAlertInformations = this.cloneValue(this.parentAlertInformations);
    clone.enabledAutoAdjustFontSize = this.cloneValue(this.enabledAutoAdjustFontSize);
    clone.autoNotePageSize = this.cloneValue(this.autoNotePageSize);
    clone.autoNotePageKind = this.cloneValue(this.autoNotePageKind);
    clone.enableNameGroupEmptyCheck = this.cloneValue(this.enableNameGroupEmptyCheck);
  }

  text(): string {
    return this.label;
  }

  get isEmpty(): boolean {
    return !this.label || this.label === "";
  }

  getGroupSelectKey(): string {
    return null;
  }

  pasteTo(item: IPasteApplicable): void {
    item.applyPastedValue(this.label, undefined);
  }

  get isSuppressionActive(): boolean {
    return this.isEmpty;
  }

  public setText(text: string): void {
    this.value = text;
    this.label = text;
  }

  adjustFontSize(): void {
    if (!this.enabledAutoAdjustFontSize) { return; }
    const adjustedFontSize = this.getAdjustedTextFontSize(this.label, this.textStyle.isMultiLine);
    this.textStyle.adjustedFontSize = adjustedFontSize;
  }

  isValueUpdated(item: any): boolean {
    // shouldPasteOnUpdatingの判定を入れないと初期化時にリスト選択が文書内転記されなくなってしまう
    if (!item || this.shouldPasteOnUpdating) { return true; }
    return this.label !== item.label
      || this.score !== item.score
      || this.code !== item.code
      || this.value !== item.value
      || this.shouldInputFree !== item.shouldInputFree; // 計算オブジェクトに渡す値が変わるので判定に入れている
  }

  getValueForExternalScript(): string {
    return this.label;
  }

  setValueFromExternalScript(value: string): void {
    const found = this.options.find(o => !!value ? o.text === value : !o.text);
    if (!found || this.shouldInputFree) {
      if (this.isFreeInputable || this.shouldInputFree) {
        for (const option of this.options) {
          option.isSelected = false;
        }
        const filledText = value ? value : "";
        this.shouldInputFree = true;
        this.label = filledText;
        this.value = filledText;
        this.score = null;
        this.code = null;
        this.adjustFontSize();
      }
      return;
    }

    for (const option of this.options) {
      option.isSelected = false;
    }
    found.isSelected = true;
    this.applyOptionValue(found);
  }

  applyOptionValue(option: TextItemOption): void {
    this.label = option.text;
    this.value = option.text;
    this.score = option.score;
    this.code = option.code;
    this.textStyle.color = option.textColor;
    this.paintStyle.color = option.backgroundColor;
    this.paintStyle.kind = TextureKinds.Fill;
    this.adjustFontSize();
  }

  handlePatientProfileInformationApplied(info: PatientProfileInformation): boolean {
    if (!info || !info.items) { return false; }
    // TODO:V1の仕様の確認
    if (!this.name || !this.isEditable) { return false; }
    const patientProfileItem = info.items.find(item => item.name === this.name);
    if (!patientProfileItem) { return false; }

    const found = this.options.find(o => o.text === patientProfileItem.value);
    if (!found) {
      // V1の挙動を確認してみたところ、フリー入力で入れた値までは対応していない模様
      return false;
    }
    for (const option of this.options) {
      option.isSelected = false;
    }
    found.isSelected = true;
    this.applyOptionValue(found);
    return true;
  }

  /**
   * V1の動作を見る限りではリスト選択・クリック選択の患者プロフィールはなさそうだが、
   * 手動で編集したときはありうるので念のため実装している。
   */
  createPatientProfileItem(): PatientProfileItem {
    // TODO:V1の仕様の確認
    if (!this.name) { return null; }
    const item = new PatientProfileItem();
    item.name = this.name;
    item.value = this.label;
    return item;
  }

  /**
   * V1の動作を見る限りではリスト選択・クリック選択の患者プロフィールはなさそうだが、
   * 仕様としてはありうるので念のため実装している。
   */
  isPatientProfileKeyMatched(key: string): boolean {
    return (this.name === key);
  }

  getDisplayName(): string {
    return this.name;
  }

  extractValueForCalculate(): any {
    // フリー入力にした際に、入力した値を計算に反映させるための対応
    return this.shouldInputFree
      ? this.label
      : ((this.score !== null && this.score !== undefined) ? this.score : this.label);
  }
}
